import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './defines/baseQueryReAuth';
import { mainServiceURL, mixgeniePublicServiceURL, mixgenieServiceURL } from './defines';

// Normalize main-service /subscriptions/current into a compact wallet shape.
// Server can return an array (multiple subs), a single object, or wrap in {data}.
const normalizeCredits = (response: any) => {
  const records = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : response?.data
        ? [response.data]
        : [];
  const active = records.find((item: any) => item?.status === 'active') || records[0] || {};
  const tokens = active.tokens || {};
  const currentPlan = Number(tokens.currentPlan ?? active.currentTokens ?? active.balance?.currentPlan ?? 0);
  const purchased = Number(tokens.purchased ?? active.balance?.purchased ?? 0);
  const total = Number(active.balance?.total ?? currentPlan + purchased);

  return {
    data: {
      creditsRemaining: Number.isFinite(total) ? total : 0,
      currentPlan: Number.isFinite(currentPlan) ? currentPlan : 0,
      purchased: Number.isFinite(purchased) ? purchased : 0,
      plan: active.plan?.name || active.planName || 'starter',
      planID: active.planID || null,
      subscriptionId: active._id || null,
      status: active.status || null,
      renewalDate: active.renewalDate || null,
      isTrial: active.providerMetadata?.isTrial === true,
      trialEndsAt: active.providerMetadata?.trialEndsAt || null,
    },
  };
};

export type CreditPack = {
  _id: string;
  code: string;
  name: string;
  type: 'token';
  amount: number; // credits
  cost: number; // in currency units (EUR)
  currency: string;
  metadata?: {
    subscription?: { priceID?: string; productID?: string };
  };
};

export type SubPlan = {
  _id: string;
  code: string;
  name: string;
  type: 'flatFee' | 'tokens';
  nTokens: number; // monthly allowance for flatFee, one-time for tokens
  trial?: boolean;
  metadata?: {
    subscription?: {
      productID?: string;
      monthly?: { priceID?: string; cost?: number };
      annual?: { priceID?: string; cost?: number };
      trialPeriodDays?: number;
    };
  };
};

// ---------------------------------------------------------------------------
// mixgenieApi — product business logic (jobs, analytics, thin credits shim)
// Generation jobs and analytics call mixgenie-service via the main-service gateway.
// Billing (packs / plans / checkout) lives in subscriptionsApi below.
// ---------------------------------------------------------------------------
export type PresetDoc = {
  _id: string;
  target: 'sound' | 'lyrics';
  category: string;
  name: string;
  promptFragment: string;
  imageUrl?: string | null;
  emoji?: string;
  gradient?: string;
  order?: number;
};

export const mixgenieApi = createApi({
  reducerPath: 'mixgenieApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Credits', 'Jobs', 'Collections'],
  endpoints: (builder) => ({
    // Credits balance — hits main-service, authenticated by cookie.
    getCreditsBalance: builder.query<any, void>({
      query: () => `${mainServiceURL}/subscriptions/current`,
      transformResponse: normalizeCredits,
      providesTags: ['Credits'],
    }),
    // Jobs — main-service's authenticated gateway prepends the :app segment
    // automatically when forwarding (Settings.apis[mixgenie-service].app=true).
    // Endpoints therefore MUST NOT include a leading `/mixgenie/` — that would
    // route to `mixgenie/mixgenie/…` on the downstream service and 404.
    createJob: builder.mutation<any, any>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/generation-jobs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobs'],
    }),
    getJob: builder.query<any, string>({
      query: (jobId) => `${mixgenieServiceURL}/generation-jobs/${jobId}`,
    }),
    listJobs: builder.query<any, { email: string; limit?: number; offset?: number }>({
      query: ({ email, limit = 20, offset = 0 }) =>
        `${mixgenieServiceURL}/generation-jobs/user/${encodeURIComponent(email)}?limit=${limit}&offset=${offset}`,
      providesTags: ['Jobs'],
    }),
    // Draft lyrics via PromptLab (text LLM). Returns { lyrics: string | null }.
    // Called from the "Generate lyrics" button so the user can edit them
    // before submitting to Mureka.
    draftLyrics: builder.mutation<any, {
      brief?: string;
      prompt?: string;
      referenceTitle?: string;
      referenceArtist?: string;
      language?: string;
    }>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/generation-jobs/lyrics`,
        method: 'POST',
        body,
      }),
    }),
    // Generate (or regenerate) the song's cover art — free, server-side via
    // the shared platform image engine.
    generateCover: builder.mutation<any, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}/cover`,
        method: 'POST',
      }),
      invalidatesTags: ['Jobs'],
    }),
    // Studio v1 — kick off Demucs stem separation on a completed song.
    // The BE returns immediately with `stems.status = 'processing'`; the
    // Studio page then polls useGetJobQuery every 3s until `stems.status`
    // flips to 'ready' (URLs populated) or 'failed'. Charges 1 credit on
    // success (nothing on failure).
    separateStems: builder.mutation<any, { jobId: string; stemModel?: string }>({
      query: ({ jobId, stemModel }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}/stems`,
        method: 'POST',
        body: stemModel ? { stemModel } : {},
      }),
      invalidatesTags: ['Jobs'],
    }),
    // "Split stems from a file" entry — user uploaded an arbitrary audio
    // file (mp3/wav/m4a) on the composer. BE creates a synthetic
    // upload-only job with the file as source and kicks off Mureka
    // stem separation. Returns the new jobId so the FE navigates to
    // /studio/:newJobId immediately.
    stemsFromUpload: builder.mutation<any, {
      audioBase64: string;
      fileName?: string;
      mimeType?: string;
      title?: string;
      stemModel?: string;
    }>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/generation-jobs/stems-from-upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobs'],
    }),
    // Studio caches the stem file names on the job doc after the first
    // ZIP unzip so subsequent visits render the mixer immediately (no
    // need to wait for the client-side unzip to identify channels).
    cacheStemNames: builder.mutation<any, { jobId: string; names: string[] }>({
      query: ({ jobId, names }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}/stems/names`,
        method: 'PATCH',
        body: { names },
      }),
    }),
    // "Inspired by an image" — vision model reads the image and drafts
    // a full song brief (style prompt + lyrics + suggested title). The
    // FE pre-fills the generator with the result; user reviews before
    // hitting Generate.
    inspireFromImage: builder.mutation<{
      data: {
        description: string;
        mood: string;
        genre: string;
        era: string;
        suggestedTitle: string;
        stylePrompt: string;
        lyrics: string;
      };
    }, { imageDataUrl: string; language?: string }>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/generation-jobs/inspire-from-image`,
        method: 'POST',
        body,
      }),
    }),
    // Set a custom cover from a user-uploaded image (gallery or camera).
    // Data URL over JSON matches the reference-audio upload pattern —
    // avoids multipart plumbing and stays under the 20 MB body cap.
    uploadCover: builder.mutation<any, { jobId: string; imageDataUrl: string }>({
      query: ({ jobId, imageDataUrl }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}/cover/upload`,
        method: 'POST',
        body: { imageDataUrl },
      }),
      invalidatesTags: ['Jobs'],
    }),
    // Rename a song (owner-only, enforced server-side).
    renameJob: builder.mutation<any, { jobId: string; title: string }>({
      query: ({ jobId, title }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: ['Jobs'],
    }),
    // Heart toggle on a generated song — same PATCH endpoint as rename,
    // just a different body key. Keeping the mutation dedicated so the
    // component can show its own busy state without conflating with rename.
    toggleJobFavourite: builder.mutation<any, { jobId: string; favourited: boolean }>({
      query: ({ jobId, favourited }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}`,
        method: 'PATCH',
        body: { favourited },
      }),
      invalidatesTags: ['Jobs'],
    }),
    // Hard-delete a song (owner-only). Backend also drops the S3 audio
    // object best-effort and pulls the song from any playlists.
    deleteJob: builder.mutation<any, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `${mixgenieServiceURL}/generation-jobs/${jobId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobs', 'Collections'],
    }),
    // Analytics
    trackEvent: builder.mutation<any, { event: string; email?: string; payload?: any }>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/analytics/track`,
        method: 'POST',
        body,
      }),
    }),

    // Song collections (playlists) — user-scoped.
    listCollections: builder.query<any[], void>({
      query: () => `${mixgenieServiceURL}/user/collections`,
      transformResponse: (r: any) => {
        const arr = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        // Normalise _id to a hex string. The BE now does this too, but
        // older BE builds (pre-2026-10-04) sometimes returned the raw
        // BSON ObjectId, which JSON-serialised into { buffer: ... } here
        // → the UI then put "[object Object]" into DELETE /collections/
        // and the backend bounced it as "Invalid id".
        return arr.map((c: any) => ({
          ...c,
          _id: c?._id?.toString ? c._id.toString() : String(c?._id || ''),
          songIds: Array.isArray(c?.songIds)
            ? c.songIds.map((x: any) => (x?.toString ? x.toString() : String(x || '')))
            : [],
        }));
      },
      providesTags: ['Collections'],
    }),
    createCollection: builder.mutation<any, { name: string }>({
      query: (body) => ({
        url: `${mixgenieServiceURL}/user/collections`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Collections'],
    }),
    renameCollection: builder.mutation<any, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `${mixgenieServiceURL}/user/collections/${id}`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Collections'],
    }),
    deleteCollection: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `${mixgenieServiceURL}/user/collections/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collections'],
    }),
    addSongToCollection: builder.mutation<any, { collectionId: string; jobId: string }>({
      query: ({ collectionId, jobId }) => ({
        url: `${mixgenieServiceURL}/user/collections/${collectionId}/songs/${jobId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Collections'],
    }),
    removeSongFromCollection: builder.mutation<any, { collectionId: string; jobId: string }>({
      query: ({ collectionId, jobId }) => ({
        url: `${mixgenieServiceURL}/user/collections/${collectionId}/songs/${jobId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collections'],
    }),

    // Presets — public catalog of one-tap prompt fragments (styles,
    // instruments, moods, lyric occasions). Unauthenticated so anonymous
    // users see the picker on the marketing pages too. We MUST send this
    // with `credentials: 'omit'` — the public gateway responds with
    // `Access-Control-Allow-Origin: *` and the CORS spec forbids that
    // combined with the default `credentials: 'include'` from the base
    // query, so the browser silently drops the response and the picker
    // renders empty.
    listPresets: builder.query<
      { groups: Array<{ target: 'sound' | 'lyrics'; category: string; presets: any[] }>; count: number },
      void
    >({
      query: () => ({
        url: `${mixgeniePublicServiceURL}/presets`,
        credentials: 'omit',
      }),
      transformResponse: (r: any) => r?.data || { groups: [], count: 0 },
    }),

    // Widget → generator hand-off. Third-party widgets stash the visitor's
    // inputs at POST /public/embed-pending → { id }; when the app opens
    // /generator?pending=<id> we read that payload here and prefill the
    // form. Credentials omitted for the same wildcard-CORS reason as
    // listPresets — the endpoint doesn't need session auth either.
    getEmbedPending: builder.query<EmbedPendingDto | null, string>({
      query: (id) => ({
        url: `${mixgeniePublicServiceURL}/embed-pending/${encodeURIComponent(id)}`,
        credentials: 'omit',
      }),
      transformResponse: (r: any) => r?.data || null,
    }),
  }),
});

export interface EmbedPendingDto {
  id: string;
  mode: 'prompt' | 'reference' | 'lyrics' | 'image' | 'audio';
  prompt?: string;
  lyrics?: string;
  referenceSongCatalogId?: string;
  referenceSongTitle?: string;
  referenceArtistName?: string;
  referenceCoverUrl?: string;
  referenceSongSlug?: string;
  referenceArtistSlug?: string;
  imageDataUrl?: string;
  referenceAudioDataUrl?: string;
  referenceAudioFileName?: string;
  referenceAudioMimeType?: string;
  language?: string;
  instrumental?: boolean;
  source?: string;
}

// ---------------------------------------------------------------------------
// subscriptionsApi — billing lifecycle lives entirely in main-service.
//   list-packs / list-plans / start-checkout (pack or sub) / billing-portal.
// Stripe webhooks (on main-service) actually grant credits and create subs;
// FE just kicks off the flow and reads the wallet after redirect-back.
// ---------------------------------------------------------------------------
export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Subscription', 'CreditPacks', 'SubPlans'],
  endpoints: (builder) => ({
    // Catalog — one-time credit packs. Server sorts by amount ASC.
    getCreditPacks: builder.query<CreditPack[], void>({
      query: () => `${mainServiceURL}/products/type/token`,
      transformResponse: (r: any): CreditPack[] => (Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : []),
      providesTags: ['CreditPacks'],
    }),
    // Catalog — recurring subscription plans (flatFee type).
    getSubPlans: builder.query<SubPlan[], void>({
      query: () => `${mainServiceURL}/plans`,
      transformResponse: (r: any): SubPlan[] => {
        const rows = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        return rows.filter((p: any) => p.type === 'flatFee' && p.metadata?.subscription);
      },
      providesTags: ['SubPlans'],
    }),
    // Buy a one-time credit pack — main-service creates a Stripe checkout
    // session and returns { url } to redirect to.
    createBuyCreditsSession: builder.mutation<
      { url: string } | any,
      { priceId: string; creditAmount: number; quantity?: number; returnUrl?: string }
    >({
      query: (body) => ({
        url: `${mainServiceURL}/subscriptions/buy-credits-session`,
        method: 'POST',
        body,
      }),
    }),
    // Start a subscription checkout for a plan + billing cycle.
    // main-service resolves priceID from the plan's metadata.subscription.{monthly|annual}.
    initCheckout: builder.mutation<
      { url: string } | any,
      { planID: string; billingCycle: 'monthly' | 'annual'; successUrl?: string; cancelUrl?: string }
    >({
      query: ({ planID, billingCycle, successUrl, cancelUrl }) => ({
        url: `${mainServiceURL}/subscriptions/init-subscription`,
        method: 'POST',
        body: {
          type: 'initSubscription',
          provider: 'stripe',
          metadata: {
            planInfo: { planID, billingCycle },
            successUrl,
            cancelUrl,
          },
        },
      }),
    }),
    // Full current-subscription doc (raw — different consumer than getCreditsBalance).
    getCurrentSubscription: builder.query<any, void>({
      query: () => `${mainServiceURL}/subscriptions/current`,
      providesTags: ['Subscription'],
    }),
    // Stripe billing portal — manage payment method, view invoices, cancel.
    getBillingPortalUrl: builder.mutation<any, void>({
      query: () => ({
        url: `${mainServiceURL}/subscriptions/update-billing`,
        method: 'POST',
        body: {},
      }),
    }),
  }),
});

export const {
  useGetCreditsBalanceQuery,
  useCreateJobMutation,
  useGetJobQuery,
  useListJobsQuery,
  useTrackEventMutation,
  useRenameJobMutation,
  useToggleJobFavouriteMutation,
  useGenerateCoverMutation,
  useSeparateStemsMutation,
  useStemsFromUploadMutation,
  useCacheStemNamesMutation,
  useUploadCoverMutation,
  useInspireFromImageMutation,
  useDeleteJobMutation,
  useDraftLyricsMutation,
  useListCollectionsQuery,
  useCreateCollectionMutation,
  useRenameCollectionMutation,
  useDeleteCollectionMutation,
  useAddSongToCollectionMutation,
  useRemoveSongFromCollectionMutation,
  useListPresetsQuery,
  useLazyGetEmbedPendingQuery,
} = mixgenieApi;

export const {
  useGetCreditPacksQuery,
  useGetSubPlansQuery,
  useCreateBuyCreditsSessionMutation,
  useInitCheckoutMutation,
  useGetCurrentSubscriptionQuery,
  useGetBillingPortalUrlMutation,
} = subscriptionsApi;
